'use strict';

// Identical browser/server module: keep publication and editor feedback aligned.
const text = (value) => typeof value === 'string' ? value.trim() : '';
const list = (value) => Array.isArray(value) ? value : [];
const unique = (value) => [...new Set(list(value).map(text).filter(Boolean))];

function visitorFacingFields(packageData) {
  const fields = [];
  const add = (path, value) => {
    if (Array.isArray(value)) value.forEach((item, index) => add(`${path}[${index}]`, item));
    else if (text(value)) fields.push({ path, text: text(value) });
  };
  const collection = (key, keys) => list(packageData?.[key]).forEach((item, index) => {
    keys.forEach((field) => add(`${key}[${index}].${field}`, item?.[field]));
  });
  collection('topics', ['label']);
  collection('visitor_topic_prompts', ['title', 'description', 'places']);
  collection('story_promises', ['label', 'description', 'primary_question', 'secondary_questions']);
  collection('story_opportunities', ['discovery_title', 'teaser', 'central_question', 'visual_cues']);
  list(packageData?.story_opportunities).forEach((item, index) => {
    list(item?.visitor_lenses).forEach((lens, lensIndex) => {
      ['central_question', 'fit_reason'].forEach((field) => {
        add(`story_opportunities[${index}].visitor_lenses[${lensIndex}].${field}`, lens?.[field]);
      });
    });
  });
  collection('stops', ['good_followup_questions']);
  // Proper names, source titles, quotations, and internal contribution/role notes
  // are intentionally excluded from language guessing.
  return fields;
}

function visitorLanguageReviewInput(packageData) {
  return JSON.stringify({
    language: text(packageData?.manifest?.language),
    fields: visitorFacingFields(packageData),
  });
}

const ENGLISH_WORDS = new Set('the how what why when where which whose who with from became becomes become did does is are was were their your our this that these those through between behind before after into without and'.split(' '));
const PORTUGUESE_WORDS = new Set('o a os as um uma de do da dos das e em no na nos nas que como porque quem qual quais quando onde entre para por pelo pela com sem ao aos à às'.split(' '));

function likelyEnglishInPortuguese(value) {
  const words = text(value).toLowerCase().match(/[\p{L}]+/gu) || [];
  const english = words.filter((word) => ENGLISH_WORDS.has(word)).length;
  const portuguese = words.filter((word) => PORTUGUESE_WORDS.has(word)).length;
  // Conservative: one shared word or a foreign proper name is insufficient.
  return english >= 2 && english > portuguese + 1 && english / words.length >= 0.15;
}

function validateAiGuideQuality(packageData, hashText) {
  const issues = [];
  const version = packageData?.manifest?.quality_contract_version;
  const strict = version === 1;
  const add = (severity, code, path, message) => issues.push({ severity, code, path, message });
  if (version != null && version !== 1) {
    add('error', 'QUALITY_VERSION_UNSUPPORTED', 'manifest.quality_contract_version', 'This quality contract version is not supported.');
  }
  if (strict && packageData?.manifest?.experience_contract_version !== 1) {
    add('error', 'EXPERIENCE_CONTRACT_REQUIRED', 'manifest.experience_contract_version', 'Quality checks require the reviewed experience contract.');
  }
  const language = text(packageData?.manifest?.language).toLowerCase();
  if (language === 'pt' || language.startsWith('pt-')) {
    visitorFacingFields(packageData).forEach((field) => {
      if (likelyEnglishInPortuguese(field.text)) {
        add(strict ? 'error' : 'warning', 'VISITOR_LANGUAGE_MISMATCH', field.path,
          'This visitor-facing text appears to be English, but the guide language is Portuguese. Translate or review this field.');
      }
    });
  }
  if (strict) {
    const review = packageData?.manifest?.visitor_language_review || {};
    const expectedHash = typeof hashText === 'function'
      ? hashText(visitorLanguageReviewInput(packageData)) : null;
    if (!expectedHash || review.reviewed_input_hash !== expectedHash) {
      add('error', 'VISITOR_LANGUAGE_REVIEW_STALE', 'manifest.visitor_language_review',
        'Visitor text changed or has not been reviewed. Review the current copy before publication.');
    }
  }
  const opportunities = list(packageData?.story_opportunities);
  if (opportunities.length >= 3 && !opportunities.some((item) => (
    item.personalization_mode === 'lenses' && list(item.visitor_lenses).length > 0
  ))) {
    add('warning', 'NO_VISITOR_LENSES', 'story_opportunities',
      'All stories use a universal explanation. Interests can choose stories, but there are no editorial lenses to adapt a selected story.');
  }
  opportunities.forEach((opportunity, index) => {
    const path = `story_opportunities[${index}]`;
    const roles = opportunity?.evidence_roles || {};
    if (!unique(roles.explanation).length) {
      add(strict ? 'error' : 'warning', 'EXPLANATION_EVIDENCE_MISSING', `${path}.evidence_roles.explanation`,
        'Select the evidence that answers this story question. Other roles are optional.');
    }
    const anchors = new Set(unique(opportunity?.anchor_stop_ids));
    const covered = new Set(list(packageData?.guide_shadow)
      .filter((shadow) => anchors.has(shadow?.stop_id))
      .flatMap((shadow) => unique(shadow?.claim_refs)));
    const variants = [{ ...opportunity, path }];
    if (opportunity?.personalization_mode === 'lenses') {
      list(opportunity?.visitor_lenses).forEach((lens, lensIndex) => variants.push({
        ...lens, path: `${path}.visitor_lenses[${lensIndex}]`,
      }));
    }
    const signatures = new Set();
    variants.forEach((variant, variantIndex) => {
      const claims = unique(variant.claim_refs);
      const overlap = claims.filter((id) => covered.has(id));
      if (claims.length && overlap.length / claims.length >= 0.6) {
        add('warning', 'RECORDING_OVERLAP', `${variant.path}.claim_refs`,
          `${overlap.length} of ${claims.length} claims are covered by connected recordings. Check the new payoff and test after listening; overlap alone does not prove repetition.`);
      }
      if (variantIndex && !unique(roles.explanation).some((id) => claims.includes(id))) {
        add(strict ? 'error' : 'warning', 'LENS_EXPLANATION_MISSING', `${variant.path}.claim_refs`,
          'This lens has no evidence assigned to explanation. Assign an explanation role to at least one of its claims.');
      }
      const signature = JSON.stringify({
        question: text(variant.central_question).toLowerCase(),
        contribution: text(variant.experience_contribution).toLowerCase(),
        claims: [...claims].sort(),
        depth: text(variant.depth) || 'standard',
      });
      if (signatures.has(signature)) {
        add(strict ? 'error' : 'warning', 'DUPLICATE_VISITOR_LENS', variant.path,
          'This lens repeats the same question, contribution, evidence, and depth. Make it useful or remove it.');
      }
      signatures.add(signature);
    });
    const lenses = list(opportunity?.visitor_lenses);
    if (lenses.length >= 2 && new Set(lenses.map((lens) => (
      JSON.stringify(unique(lens.claim_refs).sort())
    ))).size === 1) {
      add('warning', 'IDENTICAL_LENS_EVIDENCE', `${path}.visitor_lenses`,
        'Every lens uses the same evidence. Compare generated answers to confirm that the questions and explanations differ usefully.');
    }
  });
  return issues;
}

export { visitorFacingFields, visitorLanguageReviewInput, likelyEnglishInPortuguese, validateAiGuideQuality };
